import { getErrorMessage } from "../utils/errorMessage";
import React, { useMemo, useState } from "react";
import { useMasters } from "../context/MastersContext";
import { toast } from "react-toastify";
import { isPrivilegedRole } from "../utils/accessControl";
import ConfirmModal from "../components/ConfirmModal";

const CATEGORY_ORDER = [
  ["customer_country", "Customer Country"],
  ["customer_region", "Customer Region"],
  ["project_type", "Project Type"],
  ["project_ownership_Services", "Project Ownership - Services"],
  ["project_ownership_Products & IP", "Project Ownership - Products & IP"],
  ["project_ownership_Internal", "Project Ownership - Internal"],
  ["commercial_model", "Commercial Model"],
  ["billing_frequency", "Billing Frequency"],
  ["project_execution", "Project Execution"],
  ["project_location", "Project Location"],
  ["industry_vertical", "Industry Vertical"],
  ["po_sow_model", "PO / SOW Model"],
  ["project_status", "Project Status"],
  ["billing_rate_type", "Billing Rate Type"],
  ["currency", "Currency"],
  ["revenue_adjustment_reason", "Revenue Adjustment Reason"],
];

function catLabel(cat) {
  const found = CATEGORY_ORDER.find(([c]) => c === cat);
  if (found) return found[1];
  return cat
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export default function MastersTab() {
  const { byCategory, loading, error, createMaster, updateMaster } = useMasters();

  const user = JSON.parse(localStorage.getItem("user") || "null");
  const canManage = isPrivilegedRole(user?.role);

  const [newValues, setNewValues] = useState({});
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [addingCat, setAddingCat] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [itemToDeactivate, setItemToDeactivate] = useState(null);

  const categories = useMemo(() => {
    const known = CATEGORY_ORDER.map(([c]) => c);
    const keys = Object.keys(byCategory);
    const ordered = keys
      .slice()
      .sort((a, b) => {
        const ia = known.indexOf(a) === -1 ? known.length : known.indexOf(a);
        const ib = known.indexOf(b) === -1 ? known.length : known.indexOf(b);
        return ia - ib || a.localeCompare(b);
      });
    return ordered;
  }, [byCategory]);

  const setNewValue = (cat, val) => setNewValues((prev) => ({ ...prev, [cat]: val }));

  const handleAdd = async (cat) => {
    const value = (newValues[cat] || "").trim();
    if (!value) {
      toast.error("Enter a value");
      return;
    }
    setAddingCat(cat);
    setSaving(true);
    try {
      await createMaster({ category: cat, value });
      toast.success(`Added "${value}" to ${catLabel(cat)}`);
      setNewValue(cat, "");
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to add value"));
    } finally {
      setAddingCat(null);
      setSaving(false);
    }
  };

  const startEdit = (item) => setEditing({ id: item.id, category: item.category, value: item.value });
  const cancelEdit = () => setEditing(null);

  const handleSaveEdit = async (cat) => {
    if (!editing || !editing.value.trim()) {
      toast.error("Enter a value");
      return;
    }
    setSaving(true);
    try {
      await updateMaster(editing.id, { value: editing.value.trim() });
      toast.success(`Updated "${catLabel(cat)}"`);
      setEditing(null);
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to update value"));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = (item) => {
    const nextActive = !item.is_active;
    if (nextActive) {
      // activating needs no confirmation
      doToggleActive(item, nextActive);
      return;
    }
    setItemToDeactivate(item);
  };

  const doToggleActive = async (item, nextActive) => {
    setTogglingId(item.id);
    try {
      await updateMaster(item.id, { isActive: nextActive ? 1 : 0 });
      toast.success(nextActive ? `Activated "${item.value}"` : `Deactivated "${item.value}"`);
    } catch (err) {
      toast.error(err.response?.data?.error || `Failed to ${nextActive ? "activate" : "deactivate"} value`);
    } finally {
      setTogglingId(null);
    }
  };

  const confirmDeactivate = async () => {
    if (!itemToDeactivate || togglingId) return;
    const item = itemToDeactivate;
    const nextActive = false;
    setItemToDeactivate(null);
    await doToggleActive(item, nextActive);
  };

  const renderCategory = (cat, categoryIndex) => {
    const items = byCategory[cat] || [];
    const activeCount = items.filter((m) => m.is_active).length;
    const isExpanded = expandedCategory === cat;
    return (
      <section key={cat} style={{ order: categoryIndex }} className={`mst-card${isExpanded ? " mst-card-expanded" : ""}`}>
        <button
          type="button"
          className="mst-card-head"
          aria-expanded={isExpanded}
          aria-controls={`mst-panel-${categoryIndex}`}
          onClick={() => setExpandedCategory(isExpanded ? null : cat)}
        >
          <span className="mst-card-title">{catLabel(cat)}</span>
          <span className="mst-head-trailing">
            <span className="mst-count">{activeCount}</span>
            <svg className="mst-chevron" viewBox="0 0 20 20" aria-hidden="true">
              <path d="m5 7.5 5 5 5-5" />
            </svg>
          </span>
        </button>

        {isExpanded && <div className="mst-card-content" id={`mst-panel-${categoryIndex}`}>
          {canManage && (
            <div className="mst-add-row">
              <input
                className="mst-input"
                placeholder="Add new value..."
                value={newValues[cat] || ""}
                onChange={(e) => setNewValue(cat, e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleAdd(cat); }}
              />
              <button
                className="mst-btn mst-btn-primary"
                onClick={() => handleAdd(cat)}
                disabled={saving || addingCat === cat}
              >
                {addingCat === cat ? "Adding..." : "Add"}
              </button>
            </div>
          )}

          {items.length === 0 ? (
            <div className="mst-empty-sm">No values yet.</div>
          ) : (
            <table className="mst-table">
              <tbody>
                {items.map((item, i) => (
                  <tr key={item.id} className={!item.is_active ? "mst-row-inactive" : ""}>
                    <td className="mst-num">{i + 1}</td>
                    <td>
                      {editing && editing.id === item.id ? (
                        <input
                          className="mst-input mst-edit-input"
                          autoFocus
                          value={editing.value}
                          onChange={(e) => setEditing((prev) => ({ ...prev, value: e.target.value }))}
                          onKeyDown={(e) => { if (e.key === "Enter") handleSaveEdit(cat); if (e.key === "Escape") cancelEdit(); }}
                        />
                      ) : (
                        <span className={!item.is_active ? "mst-muted" : ""}>
                          {item.value}
                          {!item.is_active && <em className="mst-inactive-tag">inactive</em>}
                        </span>
                      )}
                    </td>
                    <td className="mst-actions">
                      {editing && editing.id === item.id ? (
                        <>
                          <button className="mst-link-btn" onClick={() => handleSaveEdit(cat)} disabled={saving}>Save</button>
                          <button className="mst-link-btn mst-link-muted" onClick={cancelEdit} disabled={saving}>Cancel</button>
                        </>
                      ) : (
                        <>
                          {canManage && <button className="mst-link-btn" onClick={() => startEdit(item)} disabled={Boolean(saving) || Boolean(togglingId)}>Edit</button>}
                          {canManage && (item.is_active ? (
                            <button
                              className="mst-link-btn mst-link-muted"
                              onClick={() => handleToggleActive(item)}
                              disabled={Boolean(saving) || Boolean(togglingId)}
                            >
                              {togglingId === item.id ? "Deactivating..." : "Deactivate"}
                            </button>
                          ) : (
                            <button
                              className="mst-link-btn mst-link-green"
                              onClick={() => handleToggleActive(item)}
                              disabled={Boolean(saving) || Boolean(togglingId)}
                            >
                              {togglingId === item.id ? "Activating..." : "Activate"}
                            </button>
                          ))}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>}
      </section>
    );
  };

  return (
    <div>
      <style>{css}</style>

      <div className="mst-header">
        <div>
          <h2 className="mst-title">Masters</h2>
          <p className="mst-subtitle">Manage option lists used across Projects MIS, Customers, Rate Master and Revenue pages.</p>
        </div>
      </div>

      {loading ? (
        <div className="mst-empty">Loading masters...</div>
      ) : error ? (
        <div className="mst-empty" style={{ color: "#DC2626" }}>Failed to load masters.</div>
      ) : categories.length === 0 ? (
        <div className="mst-empty">No master categories found.</div>
      ) : (
        <div className="mst-grid">
          {[0, 1].map((column) => (
            <div className="mst-column" key={column}>
              {categories.map((cat, categoryIndex) => (
                categoryIndex % 2 === column ? renderCategory(cat, categoryIndex) : null
              ))}
            </div>
          ))}
        </div>
      )}
      <ConfirmModal
        isOpen={!!itemToDeactivate}
        variant="warning"
        title="Deactivate value?"
        message={itemToDeactivate ? `Deactivate "${itemToDeactivate.value}" from ${catLabel(itemToDeactivate.category)}?` : ""}
        confirmLabel="Deactivate"
        cancelLabel="Cancel"
        isLoading={!!togglingId}
        loadingText="Deactivating..."
        onConfirm={confirmDeactivate}
        onCancel={() => setItemToDeactivate(null)}
      />
    </div>
  );
}

const css = `
  .mst-header { margin-bottom: 20px; }
  .mst-title { margin: 0; font-size: 20px; font-weight: 700; color: #1E293B; }
  .mst-subtitle { margin: 4px 0 0; font-size: 13px; color: #6B7280; }
  .mst-empty { padding: 60px 24px; text-align: center; color: #94A3B8; font-size: 14px; }
  .mst-empty-sm { padding: 16px 12px; text-align: center; color: #94A3B8; font-size: 13px; }
  .mst-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); align-items: start; gap: 14px; }
  .mst-column { display: flex; min-width: 0; flex-direction: column; gap: 14px; }
  .mst-card { min-width: 0; background: #fff; border: 1px solid #D9E2EF; border-radius: 10px; box-shadow: 0 1px 3px rgba(16,41,100,0.04); overflow: hidden; }
  .mst-card-head { width: 100%; min-height: 54px; display: flex; justify-content: space-between; align-items: center; gap: 12px; padding: 12px 16px; background: #fff; border: 0; color: inherit; text-align: left; cursor: pointer; }
  .mst-card-head:hover { background: #F8FAFC; }
  .mst-card-head:focus-visible { outline: 2px solid #1E40AF; outline-offset: -2px; }
  .mst-card-expanded .mst-card-head { background: #F8FAFC; border-bottom: 1px solid #E2E8F0; }
  .mst-card-title { min-width: 0; font-size: 13px; font-weight: 700; color: #1E293B; }
  .mst-head-trailing { display: inline-flex; flex: 0 0 auto; align-items: center; gap: 12px; }
  .mst-chevron { width: 18px; height: 18px; fill: none; stroke: #64748B; stroke-width: 1.8; stroke-linecap: round; stroke-linejoin: round; transition: transform 0.18s ease; }
  .mst-card-expanded .mst-chevron { transform: rotate(180deg); }
  .mst-card-content { min-width: 0; }
  .mst-count { background: #EFF6FF; color: #1E40AF; font-size: 11px; font-weight: 700; border-radius: 999px; padding: 2px 8px; }
  .mst-add-row { display: flex; gap: 8px; padding: 12px 16px; border-bottom: 1px solid #F1F5F9; }
  .mst-input { flex: 1; padding: 8px 12px; border: 1px solid #D9E2EF; border-radius: 8px; font-size: 13px; font-family: 'Inter', sans-serif; color: #1E293B; background: #fff; outline: none; transition: border-color 0.15s; }
  .mst-input:focus { border-color: #1E40AF; box-shadow: 0 0 0 2px rgba(30,64,175,0.1); }
  .mst-edit-input { padding: 4px 8px; }
  .mst-btn { padding: 8px 14px; border: none; border-radius: 8px; font-size: 13px; font-weight: 600; font-family: 'Inter', sans-serif; cursor: pointer; transition: all 0.15s; white-space: nowrap; }
  .mst-btn:disabled { opacity: 0.6; cursor: not-allowed; }
  .mst-btn-primary { background: linear-gradient(135deg, #1E40AF, #1e3a8a); color: #fff; }
  .mst-btn-primary:hover:not(:disabled) { box-shadow: 0 4px 12px rgba(30,64,175,0.3); }
  .mst-table { width: 100%; border-collapse: collapse; font-size: 13px; }
  .mst-table tbody td { padding: 10px 16px; border-bottom: 1px solid #F1F5F9; color: #1E293B; }
  .mst-table tbody tr:last-child td { border-bottom: none; }
  .mst-table tbody tr:hover { background: #F8FAFC; }
  .mst-num { width: 30px; color: #94A3B8; font-size: 12px; }
  .mst-actions { text-align: right; white-space: nowrap; width: 120px; }
  .mst-link-btn { background: none; border: none; color: #1E40AF; font-size: 12px; font-weight: 600; cursor: pointer; padding: 2px 6px; }
  .mst-link-btn:hover { text-decoration: underline; }
  .mst-link-muted { color: #64748B; }
  .mst-link-green { color: #059669; }
  .mst-link-danger { color: #DC2626; }
  .mst-row-inactive td { color: #94A3B8; }
  .mst-muted { color: #94A3B8; }
  .mst-inactive-tag { display: inline-block; margin-left: 6px; font-style: normal; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.3px; background: #F1F5F9; color: #94A3B8; border-radius: 999px; padding: 1px 6px; }
  @media (max-width: 768px) {
    .mst-grid { grid-template-columns: 1fr; }
    .mst-column { display: contents; }
  }
  @media (prefers-reduced-motion: reduce) {
    .mst-chevron { transition: none; }
  }
`;
